'use client';

/**
 * NotificationsPopover Component
 *
 * Topbar popover that lists recent notifications:
 *   - Header: bell icon + title + "N new" mint chip + "Mark all read" text button
 *   - Filter tabs: All · {n} / Mentions / Astor (mint-pill active state)
 *   - Vertical list of NotificationItem rows, each with a 2px left-border accent
 *     in its derived tone (mint=Astor, amber=warning, red=critical, info=default)
 *   - "Open Inbox →" footer button (callback — host wires the route)
 *   - Mint border + inset glow (matches BriefViewPopover / AstorQuickAskPopover)
 *
 * Visual spec source: design bundle shell.jsx (`NotificationsPopover`, 334–388).
 * Mint glow + popover shape conventions mirror BriefViewPopover.tsx +
 * AstorQuickAskPopover.tsx — the three popovers form one visual family.
 *
 * Host-pattern parity with BriefViewPopover: this component renders only the
 * Paper surface; the parent owns the MUI <Popover/> anchor + open state.
 *
 * @see Issue #1866 — Notifications popover (v3 redesign)
 */

import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import { alpha, type SxProps, type Theme, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { AnimatePresence, motion } from 'framer-motion';
import { type ReactNode, useId, useMemo } from 'react';
import { useI18n } from '../../hooks';
import { springPresets } from '../../theme/transitions';
import { fontSizes, iconSizes } from '../../theme/typography';
import { GlassButton } from '../GlassButton';
import { GlassChip } from '../GlassChip';
import { NotificationItem, type NotificationItemData } from '../NotificationItem';

// ============================================================================
// Types
// ============================================================================

export type NotificationsPopoverFilter = 'all' | 'mentions' | 'astor';

export interface NotificationsPopoverProps {
  /** Notifications to render (already paged/limited by the host). */
  notifications: NotificationItemData[];
  /** Currently selected filter tab. */
  activeFilter: NotificationsPopoverFilter;
  /** Called when the user clicks a filter tab. */
  onFilterChange: (filter: NotificationsPopoverFilter) => void;
  /** Unread count (drives the header chip + "Mark all read" visibility). */
  unreadCount: number;
  /** Fires when a row is clicked. Host owns mark-read + navigation. */
  onNotificationClick?: (notification: NotificationItemData) => void;
  /** Fires when "Mark all read" is clicked. Absent → header button hidden. */
  onMarkAllRead?: () => void;
  /** Fires when "Open Inbox" is clicked. Absent → footer hidden. */
  onOpenInbox?: () => void;
  /** Whether notifications are still loading. Shown as muted body line. */
  loading?: boolean;
  /** Optional override for the empty-list copy. */
  emptyState?: ReactNode;
  /** Style overrides for the Paper surface. */
  sx?: SxProps<Theme>;
}

// ============================================================================
// Tone derivation
// ============================================================================

type NotificationTone = 'mint' | 'amber' | 'red' | 'info';

/**
 * Pick the tone for a notification's left-border accent + icon background.
 *
 * Order:
 *   1. Critical / urgent severity → red.
 *   2. Warning severity → amber.
 *   3. Astor agent origin → mint.
 *   4. Success severity → mint.
 *   5. Anything else → info (the neutral default in the design).
 *
 * Severity comparison is lowercased + string-coerced so we tolerate the
 * various shapes the backend NotificationSeverity enum ships in (numeric,
 * uppercase, snake_case).
 */
function deriveTone(notification: NotificationItemData): NotificationTone {
  const severity = String(notification.severity ?? '').toLowerCase();
  if (severity === 'critical' || severity === 'urgent' || severity === 'error') {
    return 'red';
  }
  if (severity === 'warning' || severity === 'warn') {
    return 'amber';
  }
  const type = String(notification.type ?? '').toLowerCase();
  if (type === 'agent') {
    return 'mint';
  }
  if (severity === 'success') {
    return 'mint';
  }
  return 'info';
}

// ============================================================================
// Filter helpers
// ============================================================================

// Single pass over notifications returning the filtered list for the active
// tab plus the counts for all three tabs. Avoids three separate filter passes
// per render (one per tab) when the list mutates.
function partitionNotifications(
  notifications: NotificationItemData[],
  activeFilter: NotificationsPopoverFilter,
): { filtered: NotificationItemData[]; counts: Record<NotificationsPopoverFilter, number> } {
  const filtered: NotificationItemData[] = [];
  const counts: Record<NotificationsPopoverFilter, number> = { all: 0, mentions: 0, astor: 0 };
  for (const n of notifications) {
    counts.all += 1;
    const isAstor = String(n.type).toLowerCase() === 'agent';
    if (isAstor) counts.astor += 1;
    if (n.isMention) counts.mentions += 1;
    if (
      activeFilter === 'all' ||
      (activeFilter === 'astor' && isAstor) ||
      (activeFilter === 'mentions' && n.isMention)
    ) {
      filtered.push(n);
    }
  }
  return { filtered, counts };
}

// ============================================================================
// Component
// ============================================================================

export function NotificationsPopover({
  notifications,
  activeFilter,
  onFilterChange,
  unreadCount,
  onNotificationClick,
  onMarkAllRead,
  onOpenInbox,
  loading = false,
  emptyState,
  sx,
}: NotificationsPopoverProps) {
  const theme = useTheme();
  const { t } = useI18n();
  const isDark = theme.palette.mode === 'dark';
  const titleId = useId();

  // `primary.main` is the canonical F.mint (#4ED9A4) in the dark palette;
  // `success.main` is an intentionally offset mint-family shade. Use primary
  // here so the v3 mint accents match the rest of the chrome (Sidebar active
  // state, Brief/Astor pills, etc.).
  const mint = theme.palette.primary.main;
  const mintBorder = alpha(mint, isDark ? 0.22 : 0.18);
  const mintInsetGlow = alpha(mint, isDark ? 0.06 : 0.05);
  const mintDim = alpha(mint, isDark ? 0.18 : 0.14);

  const tonePalette = useMemo<Record<NotificationTone, string>>(
    () => ({
      mint,
      amber: theme.palette.warning.main,
      red: theme.palette.error.main,
      info: theme.palette.info?.main ?? theme.palette.primary.main,
    }),
    [mint, theme.palette.warning.main, theme.palette.error.main, theme.palette.info, theme.palette.primary.main],
  );

  const { filtered: filteredNotifications, counts: filterCounts } = useMemo(
    () => partitionNotifications(notifications, activeFilter),
    [notifications, activeFilter],
  );

  const filterTabs = useMemo<Array<{ key: NotificationsPopoverFilter; label: string; count: number }>>(
    () => [
      { key: 'all', label: t('notification.popover.filter.all'), count: filterCounts.all },
      { key: 'mentions', label: t('notification.popover.filter.mentions'), count: filterCounts.mentions },
      { key: 'astor', label: t('notification.popover.filter.astor'), count: filterCounts.astor },
    ],
    [filterCounts, t],
  );

  const showMarkAllRead = unreadCount > 0 && !!onMarkAllRead;

  return (
    <Paper
      role="dialog"
      aria-labelledby={titleId}
      elevation={0}
      sx={[
        {
          width: 380,
          maxWidth: '90vw',
          maxHeight: 520,
          display: 'flex',
          flexDirection: 'column',
          borderRadius: '11px',
          overflow: 'hidden',
          backgroundColor: 'background.paper',
          border: '1px solid',
          borderColor: mintBorder,
          boxShadow: (t) => `${t.shadows[8]}, inset 0 0 0 1px ${mintInsetGlow}`,
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {/* Header */}
      <Box
        sx={{
          px: 1.75,
          py: 1.25,
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <NotificationsOutlinedIcon
            aria-hidden
            sx={{ fontSize: iconSizes.sm, color: 'primary.main', flexShrink: 0 }}
          />
          <Typography
            id={titleId}
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              color: 'text.primary',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {t('notification.popover.title')}
          </Typography>
          {unreadCount > 0 && (
            <GlassChip
              label={t('notification.popover.unreadChip', { count: unreadCount })}
              size="small"
              color="primary"
              aria-label={t('notification.popover.unreadChip', { count: unreadCount })}
              sx={{ height: 18, fontSize: fontSizes.xs }}
            />
          )}
        </Box>
        {showMarkAllRead && (
          <GlassButton
            variant="text"
            size="small"
            onClick={onMarkAllRead}
            sx={{
              minHeight: 32,
              fontWeight: 500,
              color: 'text.secondary',
              textTransform: 'none',
              '&:hover': { color: 'text.primary', backgroundColor: 'transparent' },
            }}
          >
            {t('notification.popover.markAllRead')}
          </GlassButton>
        )}
      </Box>

      {/* Filter tabs */}
      <Box
        role="tablist"
        aria-label={t('notification.popover.filter.label')}
        sx={{
          display: 'flex',
          gap: 0.5,
          px: 1.25,
          py: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        {filterTabs.map((tab) => {
          const isActive = activeFilter === tab.key;
          return (
            <Box
              key={tab.key}
              component="button"
              type="button"
              role="tab"
              aria-selected={isActive}
              aria-controls={`${titleId}-panel`}
              onClick={() => onFilterChange(tab.key)}
              sx={{
                flex: 1,
                minHeight: 32,
                py: 0.75,
                px: 1,
                borderRadius: 1.25,
                border: 'none',
                cursor: 'pointer',
                typography: 'caption',
                fontWeight: isActive ? 600 : 500,
                bgcolor: isActive ? mintDim : 'transparent',
                color: isActive ? 'primary.main' : 'text.secondary',
                fontFamily: 'inherit',
                transition: 'background-color 120ms ease, color 120ms ease',
                '&:hover': {
                  bgcolor: isActive ? alpha(mint, isDark ? 0.22 : 0.18) : 'action.hover',
                  color: isActive ? 'primary.main' : 'text.primary',
                },
                '&:focus-visible': { outline: `2px solid ${alpha(mint, 0.5)}`, outlineOffset: 1 },
              }}
            >
              {tab.count > 0 ? `${tab.label} · ${tab.count}` : tab.label}
            </Box>
          );
        })}
      </Box>

      {/* Notification list */}
      <Box
        id={`${titleId}-panel`}
        role="tabpanel"
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          '&::-webkit-scrollbar': { width: 4 },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': {
            background: isDark ? alpha(theme.palette.common.white, 0.12) : alpha(theme.palette.common.black, 0.1),
            borderRadius: 2,
          },
        }}
      >
        {loading ? (
          <Box sx={{ py: 4, px: 2, textAlign: 'center' }}>
            <Typography variant="caption" sx={{ color: 'text.disabled' }}>
              {t('notification.popover.loading')}
            </Typography>
          </Box>
        ) : filteredNotifications.length === 0 ? (
          <Box sx={{ py: 4, px: 2, textAlign: 'center' }}>
            {emptyState ?? (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={springPresets.gentle}
              >
                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, mb: 0.5 }}>
                  {t('notification.popover.empty')}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                  {t('notification.popover.emptyDescription')}
                </Typography>
              </motion.div>
            )}
          </Box>
        ) : (
          <AnimatePresence mode="popLayout">
            {filteredNotifications.map((notification) => {
              const tone = deriveTone(notification);
              const accent = tonePalette[tone];
              return (
                <Box
                  key={notification.id}
                  sx={{
                    borderLeft: '2px solid',
                    borderLeftColor: notification.isRead ? 'transparent' : accent,
                    backgroundColor: notification.isRead ? 'transparent' : alpha(accent, isDark ? 0.05 : 0.04),
                    transition: 'background-color 120ms ease',
                  }}
                >
                  <NotificationItem notification={notification} onClick={onNotificationClick} compact />
                </Box>
              );
            })}
          </AnimatePresence>
        )}
      </Box>

      {/* Footer */}
      {onOpenInbox && (
        <Box
          sx={{
            p: 1.25,
            borderTop: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <GlassButton
            variant="text"
            size="small"
            fullWidth
            onClick={onOpenInbox}
            sx={{
              minHeight: 36,
              justifyContent: 'center',
              fontWeight: 500,
              color: 'primary.main',
              textTransform: 'none',
            }}
          >
            {t('notification.popover.openInbox')} →
          </GlassButton>
        </Box>
      )}
    </Paper>
  );
}

NotificationsPopover.displayName = 'NotificationsPopover';
